import {
  DataQualityCheckResponse,
  DataQualityReportEntry
} from "../../services/dataMartModelling.service";

/**
 * Wording and counts for the data quality check (Run › Data quality). The backend reports what it
 * could not test; this only turns each finding into a customer-facing line with the fix it needs.
 */

export type DqAttentionAction =
  | { kind: "link-dimension"; entityId: number; columnId?: number }
  | { kind: "choose-key-column"; entityId: number }
  | { kind: "include-table"; entityId: number };

export type DqAttentionItem = {
  id: string;
  severity: "warning" | "info";
  title: string;
  detail: string;
  entityId?: number;
  action?: DqAttentionAction;
};

const tableName = (qualified: string) => {
  const dot = qualified.indexOf(".");
  return dot >= 0 ? qualified.slice(dot + 1) : qualified;
};

export const buildDqAttentionItems = (report: DataQualityReportEntry[]): DqAttentionItem[] =>
  report.map((entry, index) => {
    const table = tableName(entry.entity);
    const id = `${entry.kind}-${entry.entity_id ?? entry.entity}-${entry.column_id ?? entry.column ?? index}`;
    switch (entry.kind) {
      case "unresolved_dimension_link":
        return {
          id,
          severity: "warning",
          title: `${table}.${entry.column} isn't linked to a dimension`,
          detail: "Its values won't be checked against any dimension.",
          entityId: entry.entity_id,
          action:
            entry.entity_id != null
              ? { kind: "link-dimension", entityId: entry.entity_id, columnId: entry.column_id }
              : undefined
        };
      case "dimension_key_by_convention":
        return {
          id,
          severity: "warning",
          title: `${table} has no column marked as its key`,
          detail: `Tested by the name ${table}_key. Mark the key column so the test can't miss it.`,
          entityId: entry.entity_id,
          action:
            entry.entity_id != null ? { kind: "choose-key-column", entityId: entry.entity_id } : undefined
        };
      case "excluded":
        return {
          id,
          severity: "info",
          title: `${table} is left out of data quality`,
          detail: "Include it only if its table still exists in the warehouse.",
          entityId: entry.entity_id,
          action: entry.entity_id != null ? { kind: "include-table", entityId: entry.entity_id } : undefined
        };
      case "fact_key_not_modelled":
        return {
          id,
          severity: "info",
          title: `${table} has no ${table}_key column in its model`,
          detail: "It is tested by that name; the fact table in the warehouse must have it.",
          entityId: entry.entity_id
        };
      case "no_dimension_references":
        return {
          id,
          severity: "info",
          title: `${table} has no keys linked to dimensions`,
          detail: "Only its own rows are tested.",
          entityId: entry.entity_id
        };
      default:
        return {
          id,
          severity: "info",
          title: `${table}: ${entry.detail}`,
          detail: "",
          entityId: entry.entity_id
        };
    }
  });

export type DqCheckSummary = {
  tests: number;
  tablesTested: number;
  tablesTotal: number;
  keysChecked: number;
  keysTotal: number;
  needsAttention: number;
};

export const summarizeDqCheck = (check: DataQualityCheckResponse): DqCheckSummary => {
  const excluded = check.dq_report.filter((entry) => entry.kind === "excluded").length;
  const unlinked = check.dq_report.filter((entry) => entry.kind === "unresolved_dimension_link").length;
  const keysChecked = check.dq_tables.reduce((total, table) => total + table.keys.length, 0);
  return {
    tests: check.tests,
    tablesTested: check.dq_tables.length,
    tablesTotal: check.dq_tables.length + excluded,
    keysChecked,
    keysTotal: keysChecked + unlinked,
    needsAttention: buildDqAttentionItems(check.dq_report).filter((item) => item.action).length
  };
};
